/** Left Node Library
 * (VISTOERA_CONTENT_PROJECT_DIRECT_CANVAS_AND_FULLSCREEN_CANVAS_SHELL_V1
 * §27–§32, scroll UX redone by
 * VISTOERA_CONTENT_CANVAS_MEDIA_CARD_AND_GENERATION_INTERACTION_V2 §42,
 * vertical drag-reorder added by
 * VISTOERA_PRODUCTION_CANVAS_TYPED_DATAFLOW_TEXT_ASSET_LINEAGE_HIGHLIGHT_AND_TOOL_REORDER_P0
 * §37–§44) — always-visible, full-height, icon-only rail.
 *
 * VISTOERA_PC2_CANVAS_SIDEBAR_RESET_DEFAULT_TOP_BUTTON… P0:
 * "Restore default order" is a system control under the brand mark —
 * not a bottom "…" menu next to scroll-down hints.
 */

import { DownOutlined, HolderOutlined, ReloadOutlined, UndoOutlined, UpOutlined } from '@ant-design/icons'
import { Tooltip, message } from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  getAuthMatrixRevision,
  getAuthMatrixStatus,
  retryWorkspaceAuthMatrix,
  subscribeAuthMatrix,
  type AuthMatrixStatus,
} from './authMatrixRuntime'
import {
  getNodeCatalogRevision,
  getNodeCatalogStatus,
  isNodeCreatableInCatalog,
  retryWorkspaceNodeCatalog,
  subscribeNodeCatalog,
  type NodeCatalogStatus,
} from './nodeCatalogRuntime'
import { useAuth } from '../../../AuthContext'
import { getNodeDefinition, useNodeIconRevision } from './nodeRegistry'
import { useCanvasNodeRegistryI18n } from './nodeRegistryI18n'
import { loadToolbarOrder, moveItem, resetToolbarOrderToPlatformDefault, saveToolbarOrder } from './toolbarOrder'
import { PCV_NODE_TYPE_DND_MIME, vistoeraDndProps } from './canvasDragContract'
import CanvasBrandEntry from './CanvasBrandEntry'

interface Props {
  workspaceId: string
  contentProjectId: string
  onAddNode: (nodeType: string) => void
}

export default function LeftLibraryPanel({ workspaceId, contentProjectId, onAddNode }: Props) {
  const { t } = useTranslation('canvas')
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const { me } = useAuth()
  const railRef = useRef<HTMLDivElement | null>(null)
  const [canScrollUp, setCanScrollUp] = useState(false)
  const [canScrollDown, setCanScrollDown] = useState(false)

  const [order, setOrder] = useState<string[]>(() => loadToolbarOrder())
  const orderRef = useRef(order)
  useEffect(() => {
    orderRef.current = order
  }, [order])

  // VistoeraTASK-00583 — track catalog readiness so the first paint never
  // treats the full local registry as creatable (that caused unreleased
  // icons to flash, then disappear when the API answered).
  const [catalogRev, setCatalogRev] = useState(() => getNodeCatalogRevision())
  const [catalogStatus, setCatalogStatus] = useState<NodeCatalogStatus>(() => getNodeCatalogStatus())
  const [authStatus, setAuthStatus] = useState<AuthMatrixStatus>(() => getAuthMatrixStatus())

  // When workspace Node Catalog / auth matrix arrives / revises, re-reconcile
  // so platform catalog order becomes the default base (local override
  // still wins via loadToolbarOrder reconcile). TASK-00634: auth revision
  // must re-filter the rail without a full page reload.
  useEffect(() => {
    const onCatalog = () => {
      setCatalogRev(getNodeCatalogRevision())
      setCatalogStatus(getNodeCatalogStatus())
      setOrder(loadToolbarOrder())
    }
    const onAuth = () => {
      setCatalogRev(getNodeCatalogRevision() + getAuthMatrixRevision())
      setAuthStatus(getAuthMatrixStatus())
      setOrder(loadToolbarOrder())
    }
    const unsubCatalog = subscribeNodeCatalog(onCatalog)
    const unsubAuth = subscribeAuthMatrix(onAuth)
    return () => {
      unsubCatalog()
      unsubAuth()
    }
  }, [])

  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const dragOverRef = useRef<number | null>(null)

  const startReorder = useCallback((e: ReactMouseEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragFromIndex(index)
    setDragOverIndex(index)
    dragOverRef.current = index
  }, [])

  useEffect(() => {
    if (dragFromIndex === null) return
    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const wrap = el?.closest('[data-toolbar-index]') as HTMLElement | null
      if (!wrap) return
      const idx = Number(wrap.dataset.toolbarIndex)
      if (!Number.isNaN(idx) && idx !== dragOverRef.current) {
        dragOverRef.current = idx
        setDragOverIndex(idx)
      }
    }
    const onUp = () => {
      const from = dragFromIndex
      const to = dragOverRef.current
      if (from !== null && to !== null && to !== from) {
        const next = moveItem(orderRef.current, from, to)
        setOrder(next)
        saveToolbarOrder(next)
      }
      setDragFromIndex(null)
      setDragOverIndex(null)
      dragOverRef.current = null
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragFromIndex])

  const handleResetOrder = useCallback(() => {
    try {
      // Clear user override — do NOT snapshot platform order into storage.
      const next = resetToolbarOrderToPlatformDefault()
      setOrder(next)
      message.success(t('chrome.library.resetToolbarOrderSuccess'))
    } catch {
      message.error(t('chrome.library.resetToolbarOrderFailed'))
    }
  }, [t])

  const iconRev = useNodeIconRevision()
  const items = useMemo(() => {
    // Catalog LOADING/ERROR or auth idle/loading/ERROR: no unconfirmed icons
    // (TASK-00635: auth ERROR must not fall back to full catalog).
    if (catalogStatus !== 'ready' || authStatus !== 'ready') return []
    return order
      .filter((nodeType) => isNodeCreatableInCatalog(nodeType))
      .map((nodeType) => getNodeDefinition(nodeType))
      .filter(Boolean)
  }, [order, iconRev, catalogRev, catalogStatus, authStatus])

  const updateScrollHints = useCallback(() => {
    const el = railRef.current
    if (!el) return
    setCanScrollUp(el.scrollTop > 1)
    setCanScrollDown(el.scrollTop + el.clientHeight < el.scrollHeight - 1)
  }, [])

  useEffect(() => {
    updateScrollHints()
    const el = railRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(updateScrollHints)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateScrollHints, items.length])

  return (
    <div className="pcv-left-rail-wrap">
      <div className="pcv-left-rail-header">
        <CanvasBrandEntry workspaceId={workspaceId} contentProjectId={contentProjectId} />
        <Tooltip title={t('chrome.library.resetToolbarOrder')} placement="right" mouseEnterDelay={0.35} mouseLeaveDelay={0}>
          <button
            type="button"
            className="pcv-lib-reset-order-btn"
            data-testid="toolbar-reset-default-order"
            onClick={handleResetOrder}
            aria-label={t('chrome.library.resetToolbarOrder')}
          >
            <UndoOutlined />
          </button>
        </Tooltip>
        <div className="pcv-left-rail-divider" aria-hidden="true" />
        {catalogStatus === 'error' ? (
          // TASK-00576: a failed catalog load is shown (never "everything enabled")
          // and can be retried without reloading the page.
          <Tooltip title={t('chrome.library.catalogLoadFailed')} placement="right">
            <button
              type="button"
              className="pcv-lib-reset-order-btn"
              data-testid="canvas-catalog-retry"
              aria-label={t('chrome.library.catalogRetry')}
              onClick={() => void retryWorkspaceNodeCatalog(workspaceId, me?.user.id)}
            >
              <ReloadOutlined />
            </button>
          </Tooltip>
        ) : null}
        {authStatus === 'error' ? (
          // TASK-00635: auth /me failure is NOT "no permission" — prompt retry.
          <Tooltip title={t('chrome.library.authLoadFailed')} placement="right">
            <button
              type="button"
              className="pcv-lib-reset-order-btn"
              data-testid="canvas-auth-matrix-rail-retry"
              aria-label={t('chrome.library.authRetry')}
              onClick={() => void retryWorkspaceAuthMatrix(workspaceId, me?.user.id)}
            >
              <ReloadOutlined />
            </button>
          </Tooltip>
        ) : null}
      </div>

      <div className="pcv-left-rail-body">
        {canScrollUp ? (
          <div className="pcv-left-rail-more pcv-left-rail-more--top" aria-hidden="true">
            <UpOutlined />
          </div>
        ) : null}
        <div
          className="pcv-left-rail nowheel"
          ref={railRef}
          onScroll={updateScrollHints}
          data-catalog-status={catalogStatus}
          data-testid="canvas-left-library-rail"
        >
          {items.map((def, index) => {
            const localizedLabel = nodeLabel(def.nodeType, def.label)
            return (
              <div
                key={def.nodeType}
                className={`pcv-lib-item-wrap${dragOverIndex === index && dragFromIndex !== null ? ' pcv-lib-item-wrap--over' : ''}${
                  dragFromIndex !== null && dragOverIndex !== null && dragOverIndex === index && dragOverIndex < dragFromIndex
                    ? ' pcv-lib-item-wrap--indicator-above'
                    : ''
                }${
                  dragFromIndex !== null && dragOverIndex !== null && dragOverIndex === index && dragOverIndex > dragFromIndex
                    ? ' pcv-lib-item-wrap--indicator-below'
                    : ''
                }`}
                data-toolbar-index={index}
                data-node-type={def.nodeType}
                data-icon-ref={def.iconKey}
              >
                <Tooltip title={localizedLabel} placement="right" mouseEnterDelay={0.35} mouseLeaveDelay={0} destroyTooltipOnHide>
                  <div
                    className={`pcv-lib-item${dragFromIndex === index ? ' is-dragging' : ''}`}
                    data-node-type={def.nodeType}
                    data-icon-ref={def.iconKey}
                    data-testid={`toolbar-node-${def.nodeType}`}
                    {...vistoeraDndProps('palette-node')}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(PCV_NODE_TYPE_DND_MIME, def.nodeType)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onClick={() => onAddNode(def.nodeType)}
                    aria-label={localizedLabel}
                    role="button"
                  >
                    <span
                      className="pcv-lib-drag-handle"
                      draggable={false}
                      onMouseDown={(e) => startReorder(e, index)}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={t('chrome.library.dragReorder')}
                      title={t('chrome.library.dragReorder')}
                    >
                      <HolderOutlined />
                    </span>
                    <span className="pcv-lib-item-icon" style={{ color: def.color, background: `${def.color}22` }}>
                      {def.icon}
                    </span>
                  </div>
                </Tooltip>
              </div>
            )
          })}
        </div>
        {canScrollDown ? (
          <div className="pcv-left-rail-more pcv-left-rail-more--bottom" aria-hidden="true">
            <DownOutlined />
          </div>
        ) : null}
      </div>
    </div>
  )
}
